import React, { useState, useRef, useEffect, useContext } from "react";
import { useIntl } from "react-intl";
import { Switch, Spin, Text, Tooltip } from "react_reusable_components";
import { TwoColumn, TwoRow } from "../../../core/layouts";

import OperationAuditGraph from "../DashboardGraphs/Graphs/Operation/OperationAuditGraph";
import ThreeColumn from "../../../core/layouts/ThreeColumn";
import LegendMenubar from "./LegendMenuBar/LegendMenubarContainer";
import useResponsive from "../../../core/hooks/useResponsive";
import variables from "../../../themes/base/styles/variables";
import TableActions from "../../../components/TableActions";
import DashboardService from "../../../services/dashboard-service";
import { DashboardContext } from "../../../globalContext/dashboard/dashboardProvider";
import { GlobalLoaderContext } from "../../../globalContext/globalLoader/globalLoaderProvider";
import {
  getConditionalContent,
  getDashboardQueryParams,
  getLocalizedMessage,
} from "../../../UtilityFunctions/utils";
import { BAD_REQUEST } from "../../../constants/errorConstants";
import { InfoCircleOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import GraphTooltipContainer from "../../GraphTooltipContainer";
import { GRAPH_TOOLTIP } from "../../../constants";

export const OperationAuditStyles = {
  operationAuditBox: {
    width: variables.fullWidth,
    height: variables.fullHeight,
    padding: variables.paddingSmall,
    border: variables.border,
    backgroundColor: variables.bodyBg,
    minHeight: variables.graphsMinHeight,
  },
  heading: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  headerSection: {
    display: variables.dFlex,
    justifyContent: variables.spaceBetween,
    alignItems: "start",
  },
  switch: {
    gap: variables.gapXS1,
    fontWeight: variables.switchLabelFontWeight,
    spaceBetween: variables.spaceBetween,
    alignItems: variables.alignCenter,
  },
  switchIcon: {
    color: variables.red2,
  },
  iconLabel: {
    gap: variables.gapXS1,
    alignItems: variables.alignCenter,
  },
  moreOutlinedIcon: {
    paddingTop: variables.moreOutlinedIconPaddingTop,
  },
  bottomSection: {
    // height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
    paddingTop: variables.paddingSmall,
  },
  dashboardBottomSection: {
    display: variables.dFlex,
    overflow: variables.overflowInitial,
    width: variables.fullWidth,
    alignItems: variables.alignCenter,
  },
  legendMenubarStyle: {
    flexDirection: variables.flexDirection,
  },
  tabletBottomSection: {
    alignCenter: variables.alignCenter,
  },
  mobileHeading: {
    width: variables.ellipsisWidth,
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  mobileBottomSection: {
    flexDirection: variables.flexDirection,
    alignItems: variables.alignCenter,
    gap: variables.gapMediumSmall,
  },
  mobileSwitch: {
    fontSize: variables.fontSizeSmall,
    iconWidth: variables.iconWidth,
  },
  spinner: {
    justifyContent: variables.alignCenter,
    paddingTop: variables.paddingTop2,
  },
  notFoundText: {
    fontSize: variables.fontSizeXxlarge,
    textAlign: variables.alignCenter,
    width: variables.fullWidth,
  },
  tabletSection: {
    gap: variables.gapSmall,
  },
  tooltipWidth: { width: variables.tooltipWidth },
};

const OperationAudit = ({ filter }) => {//NOSONAR
  const responsive = useResponsive();
  const intl = useIntl();
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardState, dashboardDispatch] = useContext(DashboardContext);
  const [operationAuditYearDataLegend, setOperationAuditYearDataLegend] =
    useState([]);
  const [operationAuditYearDataGraph, setOperationAuditYearDataGraph] =
    useState([]);
  const [isPercentage, ] = useState(false);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [dataNotFound, setDataNotFound] = useState("");
  const screenWidth = window.innerWidth;

  useEffect(() => {
    setIsLoading(true);
    let params = getDashboardQueryParams(filter);
    delete params.isPercentage;
    delete params.subProductCategoryId;
    DashboardService.getTotalAuditVsCompleteAuditData({
      dispatch: dashboardDispatch,
      intl,
      params,
    }).catch((error) => {
      if (error?.response?.data?.code === BAD_REQUEST) {
        setDataNotFound(getLocalizedMessage(intl, "dataNotAvailable.label"));
      }
    });

    setIsLoading(false);
  }, [filter]);

  useEffect(() => {
    if (dashboardState.totalAuditVsCompleteAudit?.yearData) {
      const newData = dashboardState?.totalAuditVsCompleteAudit.yearData;
      const dataLegend = getDataLegend(intl, newData);
      const dataGraph = getGraphData(intl, newData);
      setOperationAuditYearDataLegend(dataLegend);
      setOperationAuditYearDataGraph(dataGraph);
    }
  }, [dashboardState]);

  useEffect(() => {
    setIsLoading(true);
    if (checked) {
      const convertedData = operationAuditYearDataLegend.map((item) => {
        let percentage = item.totalAudit
          ? (item.completedAudit / item.totalAudit) * 100
          : 0;
        return {
          ...item,
          totalAudit: 100,
          completedAudit: percentage.toFixed(2),
        };
      });
      const newData = dashboardState?.totalAuditVsCompleteAudit?.yearData;
      const dataGraph = getGraphData(intl, newData);
      setOperationAuditYearDataLegend(convertedData);
      setOperationAuditYearDataGraph(dataGraph);
    } else {
      const newData = dashboardState?.totalAuditVsCompleteAudit?.yearData;
      const dataLegend = getDataLegend(intl, newData);
      const dataGraph = getGraphData(intl, newData);
      setOperationAuditYearDataLegend(dataLegend);
      setOperationAuditYearDataGraph(dataGraph);
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  }, [checked]);

  const downloadUserReportsHandler = () => {
    let queryParams = getDashboardQueryParams(filter);
    delete queryParams.isPercentage;
    DashboardService.dashboardDownloadReportApiCall({
      params: {
        ...queryParams,
        fileName: getLocalizedMessage(intl, "dashboard.operationAudit.label"),
        reportType: getLocalizedMessage(intl, "auditReport.label"),
      },
      intl,
      loaderDispatch: loaderDispatch,
    });
  };

  const divRef = useRef(null);
  // Use a useRef to access the Graph component
  const graphRef = useRef();

  const downloadChartImage = () => {
    const node = divRef.current;
    // Call the downloadChartImage function of the Graph component only if it is mounted
    try {
      if (node) {
        html2canvas(node).then((canvas) => {
          const link = document.createElement("a");
          link.download = getLocalizedMessage(
            intl,
            "totalAuditsVsCompleteAuditsPng.label"
          );
          link.href = canvas.toDataURL();
          link.click();
        });
      }
    } catch (error) {
      setDataNotFound(error);
    }
  };

  return (
    <TwoRow
      style={OperationAuditStyles.operationAuditBox}
      topSection={
        <TwoColumn
          style={OperationAuditStyles.headerSection}
          leftSection={
            responsive.isSm ? (
              <Text type="secondary" style={OperationAuditStyles.heading}>
                {getLocalizedMessage(intl, "dashboard.operationAudit.label")}{" "}
                <Tooltip
                  overlayInnerStyle={OperationAuditStyles.tooltipWidth}
                  title={
                    <GraphTooltipContainer
                      data={GRAPH_TOOLTIP(intl).TOTAL_AUDIT_VS_COMPLETE_AUDIT}
                    />
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Text>
            ) : (
              <Text type="secondary" style={OperationAuditStyles.mobileHeading}>
                {getLocalizedMessage(intl, "dashboard.operationAudit.label")}{" "}
                <Tooltip
                  overlayInnerStyle={OperationAuditStyles.tooltipWidth}
                  title={
                    <GraphTooltipContainer
                      data={GRAPH_TOOLTIP(intl).TOTAL_AUDIT_VS_COMPLETE_AUDIT}
                    />
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Text>
            )
          }
          rightSection={
            dataNotFound ? null : (
              <ThreeColumn
                style={OperationAuditStyles.switch}
                leftSection={
                  responsive.isMd ? (
                    <Text type="secondary" style={OperationAuditStyles.switch}>
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  ) : (
                    <Text
                      type="secondary"
                      style={OperationAuditStyles.mobileSwitch}
                    >
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  )
                }
                middleSection={
                  <Switch
                    size={getConditionalContent({
                      condition: responsive.isLg,
                      trueValue: "default",
                      falseValue: "small",
                    })}
                    style={OperationAuditStyles.switchIcon}
                    onChange={() => setChecked(!checked)}
                    checked={checked}
                  />
                }
                rightSection={
                  <TwoColumn
                    style={OperationAuditStyles.iconLabel}
                    leftSection={
                      responsive.isMd ? (
                        <Text
                          type="secondary"
                          style={OperationAuditStyles.switch}
                        >
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      ) : (
                        <Text
                          type="secondary"
                          style={OperationAuditStyles.mobileSwitch}
                        >
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      )
                    }
                    rightSection={
                      <TableActions
                        tableActionsOptions={[
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadGraph.label"
                            ),
                            action: downloadChartImage,
                          },
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadReport.label"
                            ),
                            action: downloadUserReportsHandler,
                          },
                        ]}
                      />
                    }
                  />
                }
              />
            )
          }
        />
      }
      bottomSectionStyle={
        isLoading
          ? OperationAuditStyles.spinner
          : OperationAuditStyles.bottomSection
      }
      bottomSection={
        isLoading ? (
          <Spin size={"large"} type={"default"} />
        ) : responsive.isMd ? (
          <div style={OperationAuditStyles.dashboardBottomSection} ref={divRef}>
            <OperationAuditGraph
              ref={graphRef}
              id="graph"
              data={operationAuditYearDataGraph}
              legendData={operationAuditYearDataLegend}
              isLoading={isLoading}
              width="70%"
              height={getConditionalContent({
                condition: screenWidth < 768,
                trueValue: 250,
                falseValue: 400,
              })}
              cx="50%"
              cy="50%"
              innerRadius={"47%"}
              outerRadius={"100%"}
              barSize={15}
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
              minAngle={15}
              dataKey={getLocalizedMessage(intl, "completedAuditDataKey.label")}
              cornerRadius={"50%"}
              linkLabel={getLocalizedMessage(intl, "operationAudit.link.label")}
              checked={isPercentage}
            />
            <div style={OperationAuditStyles.legendMenubarStyle}>
              <LegendMenubar
                checked={isPercentage}
                data={operationAuditYearDataLegend}
                boxColor2={variables.grey5}
                totalAudit={getLocalizedMessage(intl, "totalRepairAudit.label")}
              />
            </div>
          </div>
        ) : (
          <div style={OperationAuditStyles.dashboardBottomSection} ref={divRef}>
            <TwoRow
              style={
                responsive.isMd ? OperationAuditStyles.tabletBottomSection : {}
              }
              topSection={
                <OperationAuditGraph
                  ref={graphRef}
                  id="graph"
                  data={operationAuditYearDataGraph}
                  legendData={operationAuditYearDataLegend}
                  isLoading={isLoading}
                  width="100%"
                  height={screenWidth < 768 ? 250 : 400}
                  cx="50%"
                  cy="50%"
                  innerRadius={"47%"}
                  outerRadius={"100%"}
                  barSize={15}
                  type="number"
                  domain={[0, 100]}
                  angleAxisId={0}
                  tick={false}
                  minAngle={15}
                  dataKey={getLocalizedMessage(
                    intl,
                    "completedAuditDataKey.label"
                  )}
                  cornerRadius={"50%"}
                  linkLabel={getLocalizedMessage(
                    intl,
                    "operationAudit.link.label"
                  )}
                  checked={isPercentage}
                />
              }
              bottomSectionStyle={
                responsive.isMd
                  ? OperationAuditStyles.tabletSection
                  : OperationAuditStyles.mobileBottomSection
              }
              bottomSection={
                <LegendMenubar
                  data={operationAuditYearDataLegend}
                  boxColor2={variables.grey5}
                  totalAudit={getLocalizedMessage(
                    intl,
                    "totalRepairAudit.label"
                  )}
                />
              }
            />
          </div>
        )
      }
    />
  );
};

export default OperationAudit;

function getDataLegend(intl, newData) {
  return newData
    ? [
        {
          name: getLocalizedMessage(intl, "completedAudit.label"),
          totalAudit: newData.totalAudit,
          completedAudit: newData.totalCompletedAudit,
          fill: variables.lightPurple,
          displayName: getLocalizedMessage(intl, "totalAudit.label"),
        },
        {
          name: getLocalizedMessage(intl, "completedRepairAudit.label"),
          totalAudit: newData.repairAudit,
          completedAudit: newData.repairCompletedAudit,
          fill: variables.green,
          displayName: getLocalizedMessage(intl, "totalRepairAudit.label"),
        },
        {
          name: getLocalizedMessage(intl, "completedOperationAudit.label"),
          totalAudit: newData.operationalAudit,
          completedAudit: newData.operationalCompletedAudit,
          fill: variables.lightBlue,
          displayName: getLocalizedMessage(intl, "totalOperationalAudit.label"),
        },
      ]
    : [];
}

function getGraphData(intl, newData) {
  return newData
    ? [
        {
          name: getLocalizedMessage(intl, "completedRepairAudit.label"),
          totalAudit: newData.repairAudit,
          completedAudit:
            (newData.repairCompletedAudit / newData.repairAudit) * 100,
          fill: variables.green,
        },
        {
          name: getLocalizedMessage(intl, "completedOperationAudit.label"),
          totalAudit: newData.operationalAudit,
          completedAudit:
            newData.operationalCompletedAudit === 0
              ? 0
              : (newData.operationalCompletedAudit / newData.operationalAudit) *
                100,
          fill: variables.lightBlue,
        },
        {
          name: getLocalizedMessage(intl, "completedAudit.label"),
          totalAudit: newData.totalAudit,
          completedAudit:
            (newData.totalCompletedAudit / newData.totalAudit) * 100,
          fill: variables.lightPurple,
        },
      ]
    : [];
}
